import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon, formatNumber } from '@column/column-ui-kit';
import { Badge, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { ModalCreateStatement } from '~/components/Dialog';
import { PopoverFilterWrapper, PopoverRangepicker } from '~/components/Popover';
import { RestrictedBanner } from '~/components/RestrictedBanner';
import {
  PaginatedDataTable,
  type PaginatedDataTableRef,
  TimeHeader as TableTimeHeader,
  Timestamp as TableTimestamp,
  useTimeFormat,
} from '~/components/Table';
import { DatePreview } from '~/elements/DatePreview';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { InterestPayoutIcon } from '~/elements/icons/transfers/InterestPayout';
import { LoanDisbursementIcon } from '~/elements/icons/transfers/LoanDisbursement';
import { LoanPaymentIcon } from '~/elements/icons/transfers/LoanPayment';
import { ActivityFilterType, useQueryParams } from '~/hooks';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { BatchTransfersSheet, TransferAmount, useBatchSheet } from '~/lib/transfers';
import { formatTransferType, getTransferBadgeClassName, getTransferIcon } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { ActivityLineItem, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner, Text } from '~/styles';
import { isValidDate } from '~/util';
import { convertDatesToRange, parseDateTime } from '~/util/dateFilterHelpers';

import { BankAccountSectionProps } from './Section';

type ActivityRow = Omit<ActivityLineItem, 'description'> & {
  id: string;
  description: [string, unknown, string | undefined];
};

const DATE_FILTER_KEYS = ['effectiveAtGt', 'effectiveAtGte', 'effectiveAtLt', 'effectiveAtLte'];

const ResetFilter = styled(Button)<{ $isHidden?: boolean }>`
  padding-left: 0;
  padding-right: 0;
  margin-left: 4px;
  margin-right: 8px;

  transition: opacity 0.2s;
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

const TransferTypeChip = ({ transferType }: { transferType: string }): ReactNode => {
  switch (transferType) {
    case 'Check Deposit Batch':
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          <CheckScannerIcon />
          Check Deposit Batch
        </Badge>
      );
    case 'Loan Disbursement':
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          <LoanDisbursementIcon />
          Loan Disbursement
        </Badge>
      );
    case 'Loan Payment':
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          <LoanPaymentIcon />
          Loan Payment
        </Badge>
      );
    case 'Interest Payout':
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          <InterestPayoutIcon />
          Interest Payout
        </Badge>
      );
    default:
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          {getTransferIcon(transferType)}
          {formatTransferType(transferType as TransferEntryType)}
        </Badge>
      );
  }
};

const getDateRange = (dates: string[]) =>
  dates.length === 2
    ? convertDatesToRange({ startDateString: dates[0], endDateString: dates[1], field: 'effectiveAt' })
    : {};

export const PageBankAccountsAccountActivity: FC = () => {
  const { isAuthorized } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { bankAccount } = useOutletContext<BankAccountSectionProps>();
  const navigate = useNavigate();
  const { openBatchSheet } = useBatchSheet();
  const { queryParams, setQueryParams } = useQueryParams<ActivityFilterType>();
  const tableRef = useRef<PaginatedDataTableRef>(null);
  const [isCreateStatementOpen, setIsCreateStatementOpen] = useState(false);
  const [statementModalDates, setStatementModalDates] = useState<string[]>([]);
  const openModal = (dates: string[]) => {
    setStatementModalDates(dates);
    setIsCreateStatementOpen(true);
  };
  const closeModal = () => setIsCreateStatementOpen(false);
  const enableCustomCSVStatements = useFeatureFlag(FeatureFlag.EnableCustomCSVStatements);
  const [dateFilter, setDateFilter] = useState<string[]>(() =>
    queryParams?.effectiveAtGte && queryParams?.effectiveAtLt
      ? [parseDateTime(queryParams.effectiveAtGte).date, parseDateTime(queryParams.effectiveAtLt, true).date]
      : []
  );
  const { timeFormat, onTimeFormatChange } = useTimeFormat('bankAccountActivity');
  const dateRange = useMemo(() => getDateRange(dateFilter), [dateFilter]);

  const hasReportingPermissions = isAuthorized({
    permission: 'reporting',
    permissionLevel: 'read',
    bankAccount: { id: bankAccount.id, owners: bankAccount.owners },
  });

  const handleFetch = (params: Record<string, any>) =>
    BankAccountRepository.getActivity(bankAccount.id, { ...params, ...dateRange }).then((response) => ({
      entries: response.activityLineItems.map(
        (item): ActivityRow => ({
          ...item,
          id: item.activityLineItemId,
          description: [item.activityLineItemId, item.amount, item.description],
        })
      ),
      hasMore: response.hasMore,
    }));

  const handleDateFilterSubmit = (value: string | string[] | undefined) => {
    const dates = Array.isArray(value) ? value : value ? [value] : [];

    setDateFilter(dates);
    setQueryParams((previous) => {
      const next: Record<string, any> = { ...(previous ?? {}) };
      DATE_FILTER_KEYS.forEach((key) => delete next[key]);
      return { ...next, ...getDateRange(dates) } as ActivityFilterType;
    });
  };

  const dateFilterKey = dateFilter.join('|');
  const dateFilterKeyRef = useRef(dateFilterKey);

  useEffect(() => {
    if (dateFilterKeyRef.current === dateFilterKey) {
      return;
    }
    dateFilterKeyRef.current = dateFilterKey;
    tableRef.current?.refetch();
  }, [dateFilterKey]);

  const columns: DataTableColumnDef<ActivityRow>[] = [
    {
      header: 'Amount / Description',
      accessor: 'description',
      width: 'minmax(300px, auto)',
      className: 'min-w-0',
      cell: ({ value }) => {
        const amount = Number(value[1] ?? 0);

        return (
          <TransferAmount
            amount={Math.abs(amount)}
            currencyCode={bankAccount.currencyCode}
            description={value[2] ?? undefined}
            isAdding={amount === 0 ? undefined : amount > 0}
          />
        );
      },
    },
    {
      header: 'Type',
      accessor: 'type',
      width: 'minmax(min-content, 180px)',
      cell: ({ value }) => <TransferTypeChip transferType={String(value)} />,
    },
    {
      header: (
        <TableTimeHeader label="Settlement Date" timeFormat={timeFormat} onTimeFormatChange={onTimeFormatChange} />
      ),
      accessor: 'settlementDate',
      width: 'min-content',
      cell: ({ value }) => (isValidDate(value) ? <TableTimestamp date={value} timeFormat={timeFormat} /> : '-'),
    },
    {
      header: 'Balance',
      accessor: 'runningBalance',
      width: 'min-content',
      cell: ({ value }) => <Text size={12}>{formatNumber(Number(value ?? 0))}</Text>,
    },
  ];

  return (
    <>
      {!hasReportingPermissions && (
        <Inner pt={0}>
          <RestrictedBanner />
        </Inner>
      )}
      <Inner p={0} pt={12}>
        <PaginatedDataTable<ActivityRow>
          ref={tableRef}
          tableId="bankAccountActivity"
          fetch={handleFetch}
          onError={(error) => {
            addDangerNotification({ content: error.message });
            navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount.id}`);
          }}
          columns={columns}
          isFiltered={dateFilter.length > 0}
          empty={{
            icon: <Icon.AnimationDocument />,
            title: 'No activity found',
            description: 'No activity was found on this account.',
          }}
          filterElement={
            <>
              <PopoverFilterWrapper
                label={
                  dateFilter.length === 2 ? (
                    <DatePreview isActive selectedDates={dateFilter} format="month-name" />
                  ) : (
                    'Date'
                  )
                }
                onSubmit={handleDateFilterSubmit}
                isActive={dateFilter.length > 0}
              >
                <PopoverRangepicker date={dateFilter} />
              </PopoverFilterWrapper>
              <ResetFilter
                size="small"
                variant="subtle-primary"
                icon={<Icon.Reset />}
                iconRight
                onClick={() => handleDateFilterSubmit(undefined)}
                $isHidden={dateFilter.length === 0}
              >
                Reset filters
              </ResetFilter>
            </>
          }
          action={
            enableCustomCSVStatements && (
              <Button
                icon={<Icon.AnimationDownload />}
                size="small"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openModal(dateFilter);
                }}
              >
                Export CSV
              </Button>
            )
          }
          onRowClick={(row) => {
            if (String(row.type) === 'Check Deposit Batch') {
              openBatchSheet(row.transactionId);
              return;
            }

            if (String(row.type) === 'Loan Disbursement') {
              navigate(`${ROUTE.LOANS}/disbursements/edit/${row.transactionId}`);
              return;
            }

            if (String(row.type) === 'Loan Payment') {
              navigate(`${ROUTE.LOANS}/payments/edit/${row.transactionId}`);
              return;
            }

            if (String(row.type) === 'Interest Payout') {
              navigate(`${ROUTE.BANK_ACCOUNTS}/interest-payouts/${row.transactionId}`);
              return;
            }

            let type = String(row.type).toLowerCase().replace(/\s+/g, '-').trim();
            if (type === 'administrative') {
              type = 'admin';
            }

            navigate(`${ROUTE.TRANSFERS}/edit/${type}/${row.transactionId}`);
          }}
        />
      </Inner>
      <ModalCreateStatement
        isOpen={isCreateStatementOpen}
        onClose={closeModal}
        bankAccount={bankAccount}
        dates={statementModalDates}
      />
      <BatchTransfersSheet />
    </>
  );
};
